import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Text,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Center,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';

import { SearchIcon, RepeatIcon, AddIcon } from '@chakra-ui/icons';

import { fetchCategoriesAction } from 'store/modules/category/category-actions';

import CreateCategoryMd from 'components/category/modals/CreateCategoryMd';
import { categoryActions } from 'store/modules/category/category-slice';
import UpdateCategoryMd from 'components/category/modals/UpdateCategoryMd';
import DeleteCategoryMd from 'components/category/modals/DeleteCategoryMd';
import CategoryImage from 'components/category/CategoryImage';
import debounce from 'lodash.debounce';
import DataTable from 'react-data-table-component';
import { ReducerStatus } from 'utils/constants';
import categoryColumns from 'utils/columns/categoryColumns';

function CategoryListPage() {
  document.title = 'Skill Quest | Admin - Categories';

  const dispatch = useDispatch();

  const categories = useSelector((state) => state.category.categories);
  const status = useSelector((state) => state.category.status);
  const metaData = useSelector((state) => state.category.metaData);

  const [selectedCategory, setSelectedCategory] = useState();
  const [perPage, setPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('asc');
  const [sortBy, setSortBy] = useState('');
  const [searchText, setSearchText] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchData = useCallback(
    ({
      selectedPage,
      selectedPageSize,
      selectedOrderBy,
      selectedSortBy,
      selectedQuery,
    }) => {
      dispatch(
        fetchCategoriesAction({
          currentPage: selectedPage || 1,
          pageSize: selectedPageSize || 10,
          orderBy: selectedOrderBy,
          sortBy: selectedSortBy,
          query: selectedQuery,
        })
      );
    },
    [dispatch]
  );

  const handlePerRowsChange = (newPerPage, page) => {
    if (newPerPage === perPage) return;
    setPerPage(newPerPage);
    fetchData({
      selectedPage: page,
      selectedPageSize: newPerPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const handleSort = (column, sortDirection) => {
    if (!column?.sortField && sortDirection === orderBy) return;
    setOrderBy(sortDirection);
    setSortBy(column.sortField);
    fetchData({
      selectedPage: 1,
      selectedPageSize: perPage,
      selectedOrderBy: sortDirection,
      selectedSortBy: column.sortField,
      selectedQuery: searchText,
    });
  };

  const handlePageChange = (page) => {
    fetchData({
      selectedPage: page,
      selectedPageSize: perPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const debouncedFetchData = useMemo(
    () =>
      debounce((query) => {
        fetchData({
          selectedPage: 1,
          selectedPageSize: perPage,
          selectedQuery: query,
        });
      }, 350),
    [fetchData, perPage]
  );

  // Update search text and trigger debounced fetch
  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchData(e.target.value);
  };

  const openCategoryModal = (category) => {
    setSelectedCategory(category);
    onOpen();
  };

  const handleOpenCreateCategoryModal = () => {
    dispatch(categoryActions.toggleCreateModal(true));
  };

  const handleOpenUpdateCategoryModal = (category) => {
    dispatch(categoryActions.setSelectedCategory(category));
    dispatch(categoryActions.toggleUpdateModal(true));
  };

  const handleOpenDeleteCategoryModal = (category) => {
    dispatch(categoryActions.setSelectedCategory(category));
    dispatch(categoryActions.toggleDeleteModal(true));
  };

  // Load data on component mount
  useEffect(() => {
    fetchData({});
  }, [fetchData]);

  return (
    <Box ml={{ base: 0, md: 20 }} minH="100vh" p="10">
      <CreateCategoryMd />
      <UpdateCategoryMd />
      <DeleteCategoryMd />

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedCategory?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Category Thumbnail */}
            {selectedCategory?.thumbnailPath && (
              <Box mb={4}>
                <Box
                  w="100%"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                >
                  <CategoryImage
                    name={selectedCategory.name}
                    image={selectedCategory.thumbnailPath}
                  />
                </Box>
              </Box>
            )}

            {/* Category Description */}
            <Text>{selectedCategory?.description}</Text>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Flex my={4} align="center">
        <Text flex={1} fontSize="2xl">
          Categories
        </Text>
        <Button
          colorScheme="red"
          leftIcon={<AddIcon />}
          onClick={() => handleOpenCreateCategoryModal()}
        >
          Create
        </Button>
      </Flex>

      <Box w="100%" p={4} color="gray.500" borderWidth="1px" borderRadius="lg">
        <Flex m={2} justify={{ base: 'center', md: 'end' }}>
          <IconButton
            aria-label="Reload table"
            variant="link"
            onClick={fetchData}
            icon={<RepeatIcon />}
          />
          <InputGroup width={{ base: '100%', md: '350px' }}>
            <InputRightElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputRightElement>
            <Input
              name="query"
              placeholder="Search"
              value={searchText}
              onChange={handleSearchTextChange}
            />
          </InputGroup>
        </Flex>

        <DataTable
          theme={useColorModeValue('default', 'dark')}
          progressPending={status === ReducerStatus.LOADING}
          columns={categoryColumns({
            openCategoryModal,
            handleOpenUpdateCategoryModal,
            handleOpenDeleteCategoryModal,
          })}
          data={categories}
          paginationTotalRows={metaData?.totalItems}
          onChangeRowsPerPage={handlePerRowsChange}
          onSort={handleSort}
          onChangePage={handlePageChange}
          pagination
          paginationServer
          sortServer
          progressComponent={
            <Center my={10}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Center>
          }
        />
      </Box>
    </Box>
  );
}

export default CategoryListPage;
